import { Link, useLocation } from "react-router-dom";
import auroraLogo from "@/assets/aurora-brand-logo.svg";
import {
  UserCircle,
  Warehouse,
  Settings,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useUserRole } from "@/hooks/useUserRole";
import { EnvironmentSwitcher } from "./EnvironmentSwitcher";
import { UserProfilePopover } from "./UserProfilePopover";
import { VersionBadge, VersionTooltip } from "./VersionBadge";
import { getLastFilterUrl } from "@/hooks/useRememberFilters";
import { mainNavItems, configItems } from "./nav-items";

const userViewItems = [
  { title: "Trailer Check in/out", icon: Warehouse, path: "/yard" },
  { title: "Driver Assignment", icon: UserCircle, path: "/my-assignments" },
];

export function AppSidebar() {
  const location = useLocation();
  const { isAdmin } = useUserRole();
  const { state, toggleSidebar } = useSidebar();
  const collapsed = state === "collapsed";
  const linkTo = (path: string) => getLastFilterUrl(path);

  const visibleConfigItems = configItems.filter(item => !item.adminOnly || isAdmin);
  const isConfigActive = configItems.some(item => location.pathname === item.path);

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="p-4">
        <VersionTooltip>
          <Link to="/" className="flex items-center gap-3 group-data-[collapsible=icon]:justify-center">
            <img src={auroraLogo} alt="Aurora logo" className="h-8 w-8 object-contain shrink-0" />
            <div className="group-data-[collapsible=icon]:hidden">
              <h1 className="text-lg font-bold text-sidebar-foreground">ATMS</h1>
              <p className="text-xs text-sidebar-foreground/60">Aurora Transportation Management</p>
            </div>
          </Link>
        </VersionTooltip>
        {/* Under the logo and title, above the environment switcher. `empty:hidden`
            because staging and prod show no chip, and an empty row would otherwise
            space the header differently there than on preview and dev. Hidden on the
            icon rail, which has no room for a chip. */}
        <div className="empty:hidden group-data-[collapsible=icon]:hidden">
          <VersionBadge />
        </div>
      </SidebarHeader>

      <div className="px-4 pb-3 group-data-[collapsible=icon]:hidden">
        <EnvironmentSwitcher variant="sidebar" dark />
      </div>
      
      <SidebarContent>
        <SidebarGroup>
          
          <SidebarGroupContent>
            <SidebarMenu>
              {mainNavItems.map((item) => (
                <SidebarMenuItem key={item.path}>
                  <SidebarMenuButton
                    asChild
                    isActive={location.pathname === item.path}
                    tooltip={item.title}
                  >
                    <Link to={linkTo(item.path)}>
                      <item.icon className="h-4 w-4" />
                      <span className="flex-1">{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <Collapsible defaultOpen={isConfigActive} className="group/collapsible group-data-[collapsible=icon]:hidden">
          <SidebarGroup>
            <SidebarGroupLabel asChild className="h-8 text-sm text-sidebar-foreground/70">
              <CollapsibleTrigger className="flex w-full items-center justify-between px-2">
                <span className="flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  Configuration
                </span>
                <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
              </CollapsibleTrigger>
            </SidebarGroupLabel>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {visibleConfigItems.map((item) => (
                    <SidebarMenuItem key={item.path}>
                      <SidebarMenuButton
                        asChild
                        isActive={location.pathname === item.path}
                        tooltip={item.title}
                      >
                        <Link to={linkTo(item.path)}>
                          <item.icon className="h-4 w-4" />
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>

        {/* Collapsed-only Configuration popover trigger */}
        <SidebarGroup className="hidden group-data-[collapsible=icon]:block">
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <Popover>
                  <PopoverTrigger asChild>
                    <SidebarMenuButton
                      isActive={isConfigActive}
                      tooltip="Configuration"
                    >
                      <Settings className="h-4 w-4" />
                      <span>Configuration</span>
                    </SidebarMenuButton>
                  </PopoverTrigger>
                  <PopoverContent side="right" align="start" className="w-56 p-1">
                    <div className="flex flex-col">
                      {visibleConfigItems.map((item) => (
                        <Link
                          key={item.path}
                          to={linkTo(item.path)}
                          className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent ${
                            location.pathname === item.path ? "bg-accent" : ""
                          }`}
                        >
                          <item.icon className="h-4 w-4" />
                          <span>{item.title}</span>
                        </Link>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel>User Views</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {userViewItems.map((item) => (
                <SidebarMenuItem key={item.path}>
                  <SidebarMenuButton
                    asChild
                    isActive={location.pathname === item.path}
                    tooltip={item.title}
                  >
                    <Link to={linkTo(item.path)}>
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="p-2">
        {/* User on the left, toggle on the right, one row. Stacks on the icon rail,
            where there is no room for a row. */}
        <div className="flex items-center justify-between gap-2 group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:gap-3">
          <UserProfilePopover compact />
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleSidebar}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="h-8 w-8 shrink-0 text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
